import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Trash2, X, Loader2, Check } from "lucide-react";
import api from "@/lib/api";

/**
 * Danger Zone — platform_admin surgical delete against the small set of
 * user-writable collections in ADMIN_DELETE_ALLOWLIST on the backend. Not a
 * general-purpose admin panel; only meant for pulling one stray row at a time
 * (probe / test rows, misclassified records, one-off cleanups).
 *
 * Guardrails, top to bottom:
 *   1. platform_admin gate — parent renders this only when user.role === 'platform_admin'.
 *   2. Allow-list dropdown fetched from GET /api/admin/delete/allowed-collections
 *      so the UI can never offer a collection the server doesn't accept.
 *   3. Reason field required, ≥10 non-whitespace chars — same rule as the server.
 *   4. Confirm modal requires the operator to TYPE the target id verbatim before
 *      the destructive button unlocks. First destructive endpoint in the system
 *      + it hits production tenant data → this is a deliberate weight-match.
 *   5. Server writes a full before-snapshot to admin_audit_log. Idempotent 404.
 */
export default function DangerZone() {
  const [collections, setCollections] = useState([]);
  const [collection, setCollection] = useState("");
  const [docId, setDocId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typedConfirm, setTypedConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const loadCollections = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/delete/allowed-collections");
      setCollections(data.collections || []);
      if (data.collections?.length) setCollection((prev) => prev || data.collections[0]);
    } catch {
      setCollections([]);
    }
  }, []);
  useEffect(() => { loadCollections(); }, [loadCollections]);

  const reasonTrimmed = reason.trim();
  const formValid = collection && docId.trim().length >= 8 && reasonTrimmed.length >= 10;
  const idMatches = typedConfirm.trim() === docId.trim();

  const openConfirm = () => {
    setError("");
    setSuccessMsg("");
    if (!formValid) {
      setError("All three fields required. Reason must be ≥10 characters.");
      return;
    }
    setTypedConfirm("");
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    if (!idMatches) return;
    setDeleting(true);
    setError("");
    try {
      const { data } = await api.delete(
        `/admin/${collection}/${encodeURIComponent(docId.trim())}`,
        { data: { reason: reasonTrimmed } },
      );
      setSuccessMsg(`Deleted ${data.collection}/${data.target_id} — tenant ${data.tenant_id || "(none)"}. Audit log written.`);
      setConfirmOpen(false);
      setDocId("");
      setReason("");
      setTypedConfirm("");
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message || "Unknown error";
      if (status === 404) setError(`Not found: no document with id=${docId.trim()} in ${collection}.`);
      else if (status === 400) setError(detail);
      else if (status === 422) setError(`Validation error: ${JSON.stringify(detail)}`);
      else if (status === 403) setError("Not allowed — platform_admin role required.");
      else setError(detail);
    } finally {
      setDeleting(false);
    }
  };

  const shortId = useMemo(() => {
    const t = docId.trim();
    if (t.length <= 16) return t;
    return `${t.slice(0, 8)}…${t.slice(-6)}`;
  }, [docId]);

  return (
    <section
      data-testid="danger-zone"
      className="bg-white border-2 border-[#f4c9c9] rounded-xl p-4 sm:p-5"
    >
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle size={14} strokeWidth={2.2} className="text-[#c73535]" />
        <h3 className="font-display font-bold text-[15px] text-[#0e1420]">Danger Zone</h3>
        <span className="text-[10.5px] uppercase tracking-widest text-[#c73535] font-bold">· Platform admin</span>
      </div>
      <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mb-4">
        Surgical delete of a single row from a user-writable collection. Full before-snapshot is captured in the admin audit log. There is no undo — you&apos;d need to re-ingest via the source agent (Claire for L10, Agent 12 for kpi_jobs, Raymond for comp, etc.) or manual entry.
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div>
          <label htmlFor="dz-collection" className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Collection</label>
          <select
            id="dz-collection"
            data-testid="dz-collection"
            value={collection}
            onChange={(e) => setCollection(e.target.value)}
            className="mt-1 w-full border border-[#e7e9ee] rounded-lg px-2.5 py-1.5 text-[13px] font-mono-data bg-white outline-none focus:border-[#c73535]"
          >
            {collections.length === 0 && <option value="">(loading…)</option>}
            {collections.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label htmlFor="dz-doc-id" className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Document ID</label>
          <input
            id="dz-doc-id"
            data-testid="dz-doc-id"
            value={docId}
            onChange={(e) => setDocId(e.target.value)}
            placeholder="6f5e236d-efa1-4c00-92bc-09c66e82fae5"
            className="mt-1 w-full border border-[#e7e9ee] rounded-lg px-2.5 py-1.5 text-[13px] font-mono-data outline-none focus:border-[#c73535]"
          />
        </div>
        <div className="md:col-span-3">
          <label htmlFor="dz-reason" className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
            Reason <span className="text-[#c73535]">(required, ≥10 chars — recorded in audit log)</span>
          </label>
          <textarea
            id="dz-reason"
            data-testid="dz-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Stray probe record from schema-testing session on YYYY-MM-DD"
            className="mt-1 w-full border border-[#e7e9ee] rounded-lg px-2.5 py-1.5 text-[13px] outline-none focus:border-[#c73535] resize-y"
          />
          <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-1">
            {reasonTrimmed.length}/500 · {reasonTrimmed.length < 10 ? <span className="text-[#c73535] font-semibold">need ≥10</span> : <span className="text-[#0e8a68] font-semibold">ok</span>}
          </div>
        </div>
      </div>

      {error && (
        <div data-testid="dz-error" className="mt-3 text-[12.5px] font-mono-data text-[#c73535] bg-[#fdecec] border border-[#f4c9c9] rounded-md px-2.5 py-1.5">
          {error}
        </div>
      )}
      {successMsg && (
        <div data-testid="dz-success" className="mt-3 text-[12.5px] text-[#0e8a68] bg-[#e6f9f2] border border-[#c1eddc] rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5">
          <Check size={13} strokeWidth={2.2} /> {successMsg}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11.5px] text-[color:var(--cc-ink-muted)]">
          Confirmation modal will require typing the full ID to fire.
        </div>
        <button
          type="button"
          onClick={openConfirm}
          data-testid="dz-review-delete"
          disabled={!formValid}
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold rounded-lg px-3 py-2 bg-[#c73535] hover:bg-[#b12b2b] text-white shadow-[0_2px_6px_0_rgba(199,53,53,0.35)] disabled:opacity-45 disabled:cursor-not-allowed disabled:shadow-none"
        >
          <Trash2 size={13} strokeWidth={2} /> Review delete
        </button>
      </div>

      {confirmOpen && (
        <div
          data-testid="dz-confirm-modal"
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !deleting && setConfirmOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-[560px] shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-[#eef1f6] flex items-center justify-between bg-[#fdecec]">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} strokeWidth={2.2} className="text-[#c73535]" />
                <div className="font-display font-bold text-[17px] text-[#0e1420]">Confirm destructive delete</div>
              </div>
              <button onClick={() => !deleting && setConfirmOpen(false)} data-testid="dz-confirm-close" className="p-1.5 rounded-md hover:bg-white/50 disabled:opacity-50" disabled={deleting}>
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-[13px] text-[#0e1420] leading-[1.55]">
                About to remove <span className="font-mono-data font-bold">{shortId}</span> from <span className="font-mono-data font-bold">{collection}</span>. The row will be gone; the audit trail keeps a full snapshot.
              </div>
              <div>
                <label htmlFor="dz-confirm-input" className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
                  Type the full ID to confirm
                </label>
                <div className="mt-1 text-[11.5px] font-mono-data text-[color:var(--cc-ink-muted)] break-all bg-[#fafbfc] border border-[#e7e9ee] rounded px-2 py-1 select-all">
                  {docId.trim()}
                </div>
                <input
                  id="dz-confirm-input"
                  data-testid="dz-confirm-input"
                  autoFocus
                  autoComplete="off"
                  value={typedConfirm}
                  onChange={(e) => setTypedConfirm(e.target.value)}
                  onPaste={(e) => e.preventDefault()}
                  placeholder="Type the ID exactly (paste disabled)"
                  className={`mt-2 w-full border rounded-lg px-2.5 py-1.5 text-[13px] font-mono-data outline-none ${
                    typedConfirm.length === 0 ? "border-[#e7e9ee]" : idMatches ? "border-[#0e8a68] bg-[#e6f9f2]" : "border-[#c73535] bg-[#fdecec]"
                  }`}
                />
                <div className="mt-1 text-[11px] font-semibold">
                  {typedConfirm.length === 0 ? (
                    <span className="text-[color:var(--cc-ink-muted)]">Waiting for input</span>
                  ) : idMatches ? (
                    <span className="text-[#0e8a68] inline-flex items-center gap-1"><Check size={11} strokeWidth={2.2} /> ID matches — delete unlocked</span>
                  ) : (
                    <span className="text-[#c73535]">Does not match — button stays locked</span>
                  )}
                </div>
              </div>
              <div className="text-[11.5px] bg-[#fff6dc] border border-[#f0dfa5] text-[#8a6a1c] rounded px-2.5 py-1.5">
                <span className="font-bold">Reason (audit log):</span> {reasonTrimmed}
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => !deleting && setConfirmOpen(false)}
                  data-testid="dz-cancel"
                  disabled={deleting}
                  className="text-[13px] font-semibold px-3 py-2 rounded-lg bg-[#f5f6f8] hover:bg-[#eef1f6] text-[#0e1420] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={doDelete}
                  data-testid="dz-confirm-delete"
                  disabled={!idMatches || deleting}
                  className="inline-flex items-center gap-1.5 text-[13px] font-semibold rounded-lg px-3 py-2 bg-[#c73535] hover:bg-[#b12b2b] text-white shadow-[0_2px_6px_0_rgba(199,53,53,0.35)] disabled:opacity-45 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  {deleting ? <><Loader2 size={13} className="animate-spin" /> Deleting…</> : <><Trash2 size={13} strokeWidth={2} /> Permanently delete</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
